import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { formatDateDisplay } from './dateUtils';

/**
 * Generate a professional PDF for maintenance record
 * @param {Object} maintenance - Maintenance record object
 * @param {Object} motorcycle - Motorcycle details
 * @param {Object} lessee - Lessee details (optional)
 */
export const generateMaintenancePDF = async (maintenance, motorcycle = null, lessee = null) => {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.width;
  const pageHeight = doc.internal.pageSize.height;
  let yPos = 10;

  // Load company logo
  let logoImg = null;
  try {
    logoImg = await loadImage('/customer-assets/Ezy_Mobility_Logo_New.png');
  } catch (e) {
    try { logoImg = await loadImage('/customer-assets/Logo_New.png'); } catch (e2) { console.log('Logo not available'); }
  }

  // Helper function to add header to each page
  const addHeader = () => {
    // HEADER SECTION - Gradient background
    doc.setFillColor(139, 195, 74); // Lime green
    doc.rect(0, 0, pageWidth, 35, 'F');
    
    // Add gradient effect with lighter overlay
    doc.setFillColor(174, 213, 129); // Lighter lime
    doc.setGState(new doc.GState({opacity: 0.3}));
    doc.rect(0, 0, pageWidth, 35, 'F');
    doc.setGState(new doc.GState({opacity: 1}));
    
    // Logo
    try {
      if (logoImg) {
        doc.setFillColor(255, 255, 255);
        doc.roundedRect(8, 4, 28, 26, 2, 2, 'F');
        doc.addImage(logoImg, 'PNG', 9, 5, 26, 24);
      }
    } catch (error) {
      console.log('Logo error:', error);
    }

    // Company name in white
    doc.setFontSize(11);
    doc.setTextColor(255, 255, 255);
    doc.setFont(undefined, 'bold');
    doc.text('EZY MOBILITY SOLUTIONS MOTORCYCLE RENTAL LLC', 40, 15);
    
    // Report title in white
    doc.setFontSize(16);
    doc.setFont(undefined, 'bold');
    doc.text('MAINTENANCE RECORD', 40, 24);
    
    // Generated date in white
    doc.setFontSize(7);
    doc.setFont(undefined, 'normal');
    doc.text(`Generated: ${new Date().toLocaleString('en-AE', { timeZone: 'Asia/Dubai' })} (UAE Time)`, 40, 29);
  };

  // Helper function to add footer to each page
  const addFooter = (pageNum, totalPages) => {
    const footerY = pageHeight - 15;
    
    // Footer accent line
    doc.setDrawColor(139, 195, 74);
    doc.setLineWidth(1.5);
    doc.line(15, footerY, pageWidth - 15, footerY);
    
    // Footer text
    doc.setFontSize(7);
    doc.setTextColor(120, 120, 120);
    doc.setFont(undefined, 'normal');
    doc.text('EZY MOBILITY SOLUTIONS MOTORCYCLE RENTAL LLC - Maintenance Management System', 15, footerY + 5);
    
    // Page number
    doc.setTextColor(139, 195, 74);
    doc.setFont(undefined, 'bold');
    doc.setFontSize(8);
    doc.text(`Page ${pageNum} of ${totalPages}`, pageWidth - 30, footerY + 5);
  };

  // Add header to first page
  addHeader();
  yPos = 43;
  
  // Generate Record ID in format: EMS/DXB-2-33654/12-05-2025/10:30
  const generateRecordID = () => {
    const companyInitials = 'EMS';
    const plateNumber = maintenance.bike_plate_number || 'N/A';
    const dateParts = maintenance.maintenance_date ? maintenance.maintenance_date.split('-') : ['', '', ''];
    const formattedDate = dateParts.length === 3 ? `${dateParts[2]}-${dateParts[1]}-${dateParts[0]}` : '';
    const time = maintenance.maintenance_time || '00:00';
    return `${companyInitials}/${plateNumber}/${formattedDate}/${time}`;
  };
  
  const recordID = generateRecordID();
  
  // RECORD INFO SECTION
  doc.setFillColor(255, 255, 255);
  doc.roundedRect(10, yPos, pageWidth - 20, 22, 3, 3, 'F');
  doc.setDrawColor(0, 0, 0);
  doc.setGState(new doc.GState({opacity: 0.08}));
  doc.setLineWidth(0.1);
  doc.roundedRect(10, yPos, pageWidth - 20, 22, 3, 3, 'S');
  doc.setGState(new doc.GState({opacity: 1}));
  
  yPos += 7;
  
  // Record ID
  doc.setFontSize(7);
  doc.setTextColor(100, 100, 100);
  doc.setFont(undefined, 'bold');
  doc.text('RECORD ID', 15, yPos);
  
  doc.setFontSize(11);
  doc.setTextColor(44, 62, 80);
  doc.setFont(undefined, 'bold');
  doc.text(recordID, 15, yPos + 6);
  
  // Date & Time
  doc.setFontSize(7);
  doc.setTextColor(100, 100, 100);
  doc.setFont(undefined, 'bold');
  doc.text('DATE & TIME', pageWidth - 65, yPos);
  
  doc.setFontSize(9);
  doc.setTextColor(44, 62, 80);
  doc.setFont(undefined, 'normal');
  doc.text(`${formatDateDisplay(maintenance.maintenance_date)} at ${maintenance.maintenance_time || 'N/A'}`, pageWidth - 65, yPos + 6);

  yPos += 18;
  
  // MOTORCYCLE INFORMATION Section
  doc.setFontSize(10);
  doc.setTextColor(139, 195, 74);
  doc.setFont(undefined, 'bold');
  doc.text('MOTORCYCLE INFORMATION', 15, yPos);
  
  yPos += 8;
  
  const motorcycleInfo = [
    ['Plate Number', maintenance.bike_plate_number || 'N/A'],
    ['Model', motorcycle?.model_name || 'N/A'],
    ['Chassis Number', motorcycle?.chassis_number || 'N/A'],
    ['Color', motorcycle?.color || 'N/A']
  ];
  
  doc.autoTable({
    startY: yPos,
    head: [],
    body: motorcycleInfo,
    theme: 'grid',
    styles: { fontSize: 8, cellPadding: 3, lineColor: [232, 232, 232], lineWidth: 0.1 },
    bodyStyles: { textColor: [60, 60, 60] },
    alternateRowStyles: { fillColor: [250, 250, 250] },
    columnStyles: {
      0: { fontStyle: 'bold', cellWidth: 50, textColor: [80, 80, 80] },
      1: { cellWidth: 'auto' }
    },
    margin: { left: 15, right: pageWidth / 2 + 5 }
  });
  
  // Rider & Lessee Information (right column)
  const riderLesseeInfo = [
    ['Rider Name', maintenance.rider_name || 'N/A'],
    ['Emirates ID', maintenance.rider_emirates_id || 'N/A'],
    ['Lessee Company', maintenance.lessee_name || lessee?.company_name || motorcycle?.lessee_name || 'N/A']
  ];
  
  doc.autoTable({
    startY: yPos,
    head: [],
    body: riderLesseeInfo,
    theme: 'grid',
    styles: { fontSize: 8, cellPadding: 3, lineColor: [232, 232, 232], lineWidth: 0.1 },
    bodyStyles: { textColor: [60, 60, 60] },
    alternateRowStyles: { fillColor: [250, 250, 250] },
    columnStyles: {
      0: { fontStyle: 'bold', cellWidth: 50, textColor: [80, 80, 80] },
      1: { cellWidth: 'auto' }
    },
    margin: { left: pageWidth / 2 + 5, right: 15 }
  });
  
  yPos = doc.lastAutoTable.finalY + 12;
  
  // MAINTENANCE DETAILS Section
  doc.setFontSize(10);
  doc.setTextColor(139, 195, 74);
  doc.setFont(undefined, 'bold');
  doc.text('MAINTENANCE DETAILS', 15, yPos);
  
  yPos += 8;
  
  // Format currency with Dirham symbol (using simple text for PDF)
  const formatCurrency = (amount) => `AED ${Math.round(amount || 0).toLocaleString()}`;
  
  const maintenanceDetails = [
    ['Type', (maintenance.type || '').toUpperCase()],
    ['Technician', maintenance.technician || 'N/A'],
    ['Location', maintenance.location || 'N/A'],
    ['Status', (maintenance.status || '').toUpperCase()],
    ['Odometer Reading', maintenance.odometer_reading ? `${maintenance.odometer_reading} km` : 'N/A'],
    ['Estimate Amount', formatCurrency(maintenance.estimate_amount)],
    ['Total Cost', formatCurrency(maintenance.total_cost)]
  ];
  
  doc.autoTable({
    startY: yPos,
    head: [],
    body: maintenanceDetails,
    theme: 'grid',
    styles: { fontSize: 8, cellPadding: 3, lineColor: [232, 232, 232], lineWidth: 0.1 },
    headStyles: { fillColor: [139, 195, 74] },
    bodyStyles: { textColor: [60, 60, 60] },
    alternateRowStyles: { fillColor: [250, 250, 250] },
    columnStyles: {
      0: { fontStyle: 'bold', cellWidth: 50, textColor: [80, 80, 80] },
      1: { cellWidth: 'auto', textColor: [60, 60, 60] }
    },
    margin: { left: 15, right: 15 },
    didParseCell: function(data) {
      if (data.row.index === 6 && data.column.index === 1) {
        data.cell.styles.fontStyle = 'bold';
        data.cell.styles.fontSize = 10;
        data.cell.styles.textColor = [0, 0, 0];
      }
    }
  });
  
  yPos = doc.lastAutoTable.finalY + 12;
  
  // REPAIR ITEMS Section
  if (maintenance.repair_items && maintenance.repair_items.length > 0) {
    doc.setFontSize(10);
    doc.setTextColor(139, 195, 74);
    doc.setFont(undefined, 'bold');
    doc.text('REPAIR ITEMS', 15, yPos);
    
    yPos += 8;
    
    const repairItemsData = maintenance.repair_items.map((item, index) => [
      index + 1,
      item.item_code || '-',
      item.amc_covered ? `${item.item_name} [AMC FREE]` : item.item_name,
      item.quantity || 1,
      formatCurrency(item.rate),
      item.amc_covered ? 'AMC FREE' : formatCurrency((item.rate || 0) * (item.quantity || 1))
    ]);
    
    // Calculate AMC discount
    const subtotal = maintenance.repair_items.reduce((sum, item) => sum + ((item.rate || 0) * (item.quantity || 1)), 0);
    const amcDiscount = maintenance.repair_items
      .filter(item => item.amc_covered)
      .reduce((sum, item) => sum + ((item.rate || 0) * (item.quantity || 1)), 0);
    const adminDiscount = maintenance.admin_discount_amount || 0;
    
    // Build footer rows
    const footRows = [['', '', '', '', 'Subtotal:', formatCurrency(subtotal)]];
    if (amcDiscount > 0) {
      footRows.push(['', '', '', '', 'AMC Discount:', `- ${formatCurrency(amcDiscount)}`]);
    }
    if (adminDiscount > 0) {
      footRows.push(['', '', '', '', 'Admin Discount:', `- ${formatCurrency(adminDiscount)}`]);
    }
    footRows.push(['', '', '', '', 'TOTAL:', formatCurrency(maintenance.total_cost)]);
    
    doc.autoTable({
      startY: yPos,
      head: [['#', 'Item Code', 'Item Name', 'Qty', 'Rate', 'Amount']],
      body: repairItemsData,
      theme: 'grid',
      styles: { fontSize: 8, cellPadding: 3, lineColor: [232, 232, 232], lineWidth: 0.1 },
      headStyles: { 
        fillColor: [139, 195, 74], 
        textColor: [255, 255, 255],
        fontStyle: 'bold',
        fontSize: 9,
        halign: 'left'
      },
      alternateRowStyles: { fillColor: [250, 250, 250] },
      footStyles: { 
        fillColor: [248, 250, 247], 
        fontStyle: 'bold', 
        textColor: [0, 0, 0], 
        fontSize: 9 
      },
      foot: footRows,
      columnStyles: {
        0: { cellWidth: 12, halign: 'center' },
        1: { cellWidth: 25 },
        2: { cellWidth: 'auto' },
        3: { cellWidth: 15, halign: 'center' },
        4: { cellWidth: 30, halign: 'right' },
        5: { cellWidth: 35, halign: 'right' }
      },
      margin: { left: 15, right: 15 },
      didParseCell: function(data) {
        // Style AMC FREE cells in green
        if (data.section === 'body' && data.column.index === 5) {
          const cellText = data.cell.raw;
          if (cellText === 'AMC FREE') {
            data.cell.styles.textColor = [34, 139, 34];
            data.cell.styles.fontStyle = 'bold';
          }
        }
        if (data.section === 'body' && data.column.index === 2) {
          const cellText = String(data.cell.raw || '');
          if (cellText.includes('[AMC FREE]')) {
            data.cell.styles.textColor = [34, 139, 34];
          }
        }
        // Style discount rows in green, total row in bold
        if (data.section === 'foot') {
          const label = String(data.row.raw?.[4] || '');
          if (label.includes('Discount')) {
            data.cell.styles.textColor = [34, 139, 34];
          }
          if (label === 'TOTAL:') {
            data.cell.styles.fontSize = 10;
            data.cell.styles.fontStyle = 'bold';
          }
        }
      }
    });
    
    yPos = doc.lastAutoTable.finalY + 12;
  }
  
  // NOTES Section
  if (maintenance.notes) {
    if (yPos > pageHeight - 50) {
      doc.addPage();
      addHeader();
      yPos = 45;
    }
    
    doc.setFontSize(10);
    doc.setTextColor(139, 195, 74);
    doc.setFont(undefined, 'bold');
    doc.text('NOTES', 15, yPos);
    
    yPos += 8;
    doc.setFontSize(9);
    doc.setFont(undefined, 'normal');
    doc.setTextColor(60, 60, 60);
    const splitNotes = doc.splitTextToSize(maintenance.notes, pageWidth - 40);
    doc.text(splitNotes, 20, yPos);
    yPos += splitNotes.length * 5 + 10;
  }
  
  // PRE-MAINTENANCE PHOTOS Section
  if (maintenance.pre_maintenance_photos && maintenance.pre_maintenance_photos.length > 0) {
    // Always start photos on a new page for better presentation
    doc.addPage();
    addHeader();
    yPos = 45;
    
    doc.setFontSize(12);
    doc.setTextColor(139, 195, 74);
    doc.setFont(undefined, 'bold');
    doc.text('PRE-MAINTENANCE PHOTOS', 15, yPos);
    
    // Add subtitle with count
    doc.setFontSize(8);
    doc.setTextColor(100, 100, 100);
    doc.setFont(undefined, 'normal');
    doc.text(`(${maintenance.pre_maintenance_photos.length} photo${maintenance.pre_maintenance_photos.length > 1 ? 's' : ''})`, 75, yPos);
    
    yPos += 12;
    
    // Photo grid layout - 2 photos per row
    const photoWidth = 85;
    const photoHeight = 65;
    const photoMargin = 10;
    let col = 0;
    
    for (let i = 0; i < maintenance.pre_maintenance_photos.length; i++) {
      try {
        const photoUrl = maintenance.pre_maintenance_photos[i];
        if (photoUrl) {
          // Check if we need a new page
          if (yPos + photoHeight > pageHeight - 30) {
            doc.addPage();
            addHeader();
            yPos = 45;
            doc.setFontSize(10);
            doc.setTextColor(139, 195, 74);
            doc.setFont(undefined, 'bold');
            doc.text('PRE-MAINTENANCE PHOTOS (continued)', 15, yPos);
            yPos += 12;
            col = 0;
          }
          
          const xPos = 15 + (col * (photoWidth + photoMargin));
          
          // Add photo border/frame
          doc.setDrawColor(200, 200, 200);
          doc.setLineWidth(0.5);
          doc.rect(xPos - 2, yPos - 2, photoWidth + 4, photoHeight + 14, 'S');
          
          // Try to load and add image
          const img = await loadImage(photoUrl);
          doc.addImage(img, 'JPEG', xPos, yPos, photoWidth, photoHeight);
          
          // Add photo number label
          doc.setFontSize(8);
          doc.setTextColor(80, 80, 80);
          doc.setFont(undefined, 'normal');
          doc.text(`Photo ${i + 1}`, xPos + photoWidth / 2 - 10, yPos + photoHeight + 8);
          
          col++;
          if (col >= 2) {
            col = 0;
            yPos += photoHeight + 20;
          }
        }
      } catch (error) {
        console.error('Error loading pre-maintenance photo:', error);
        const xPos = 15 + (col * (photoWidth + photoMargin));
        
        // Draw placeholder box
        doc.setFillColor(245, 245, 245);
        doc.setDrawColor(200, 200, 200);
        doc.rect(xPos, yPos, photoWidth, photoHeight, 'FD');
        
        doc.setFontSize(8);
        doc.setTextColor(150, 150, 150);
        doc.text(`Photo ${i + 1} unavailable`, xPos + 15, yPos + photoHeight / 2);
        
        col++;
        if (col >= 2) {
          col = 0;
          yPos += photoHeight + 20;
        }
      }
    }
    
    // Move to next row if we ended mid-row
    if (col > 0) {
      yPos += photoHeight + 20;
    }
  }
  
  // POST-MAINTENANCE PHOTOS Section
  if (maintenance.post_maintenance_photos && maintenance.post_maintenance_photos.length > 0) {
    // Always start on a new page
    doc.addPage();
    addHeader();
    yPos = 45;
    
    doc.setFontSize(12);
    doc.setTextColor(139, 195, 74);
    doc.setFont(undefined, 'bold');
    doc.text('POST-MAINTENANCE PHOTOS', 15, yPos);
    
    // Add subtitle with count
    doc.setFontSize(8);
    doc.setTextColor(100, 100, 100);
    doc.setFont(undefined, 'normal');
    doc.text(`(${maintenance.post_maintenance_photos.length} photo${maintenance.post_maintenance_photos.length > 1 ? 's' : ''})`, 80, yPos);
    
    yPos += 12;
    
    // Photo grid layout - 2 photos per row
    const photoWidth = 85;
    const photoHeight = 65;
    const photoMargin = 10;
    let col = 0;
    
    for (let i = 0; i < maintenance.post_maintenance_photos.length; i++) {
      try {
        const photoUrl = maintenance.post_maintenance_photos[i];
        if (photoUrl) {
          // Check if we need a new page
          if (yPos + photoHeight > pageHeight - 30) {
            doc.addPage();
            addHeader();
            yPos = 45;
            doc.setFontSize(10);
            doc.setTextColor(139, 195, 74);
            doc.setFont(undefined, 'bold');
            doc.text('POST-MAINTENANCE PHOTOS (continued)', 15, yPos);
            yPos += 12;
            col = 0;
          }
          
          const xPos = 15 + (col * (photoWidth + photoMargin));
          
          // Add photo border/frame
          doc.setDrawColor(200, 200, 200);
          doc.setLineWidth(0.5);
          doc.rect(xPos - 2, yPos - 2, photoWidth + 4, photoHeight + 14, 'S');
          
          // Try to load and add image
          const img = await loadImage(photoUrl);
          doc.addImage(img, 'JPEG', xPos, yPos, photoWidth, photoHeight);
          
          // Add photo number label
          doc.setFontSize(8);
          doc.setTextColor(80, 80, 80);
          doc.setFont(undefined, 'normal');
          doc.text(`Photo ${i + 1}`, xPos + photoWidth / 2 - 10, yPos + photoHeight + 8);
          
          col++;
          if (col >= 2) {
            col = 0;
            yPos += photoHeight + 20;
          }
        }
      } catch (error) {
        console.error('Error loading post-maintenance photo:', error);
        const xPos = 15 + (col * (photoWidth + photoMargin));
        
        // Draw placeholder box
        doc.setFillColor(245, 245, 245);
        doc.setDrawColor(200, 200, 200);
        doc.rect(xPos, yPos, photoWidth, photoHeight, 'FD');
        
        doc.setFontSize(8);
        doc.setTextColor(150, 150, 150);
        doc.text(`Photo ${i + 1} unavailable`, xPos + 15, yPos + photoHeight / 2);
        
        col++;
        if (col >= 2) {
          col = 0;
          yPos += photoHeight + 20;
        }
      }
    }
    
    // Move to next row if we ended mid-row
    if (col > 0) {
      yPos += photoHeight + 20;
    }
  }
  
  // DISCLAIMER on last page
  if (yPos > pageHeight - 50) {
    doc.addPage();
    addHeader();
    yPos = 45;
  }
  
  // Disclaimer box
  doc.setFillColor(255, 248, 220); // Light yellow background
  doc.roundedRect(15, yPos, pageWidth - 30, 25, 2, 2, 'F');
  doc.setDrawColor(200, 180, 100);
  doc.setLineWidth(0.3);
  doc.roundedRect(15, yPos, pageWidth - 30, 25, 2, 2, 'S');
  
  yPos += 7;
  doc.setFontSize(8);
  doc.setTextColor(139, 69, 19); // Brown text
  doc.setFont(undefined, 'bold');
  doc.text('IMPORTANT NOTE:', 20, yPos);
  
  yPos += 5;
  doc.setFont(undefined, 'normal');
  doc.setFontSize(8);
  const disclaimerText = 'This is a system generated maintenance record with an estimated cost of repairs. Kindly do not consider this as an invoice.';
  const splitDisclaimer = doc.splitTextToSize(disclaimerText, pageWidth - 40);
  doc.text(splitDisclaimer, 20, yPos);
  
  // FOOTER on all pages
  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    addFooter(i, pageCount);
  }
  
  // Save PDF
  const fileName = `Maintenance_${maintenance.bike_plate_number}_${maintenance.maintenance_date}.pdf`;
  doc.save(fileName);
};

/**
 * Helper function to load image and convert to base64
 */
const loadImage = (url) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'Anonymous';
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = url;
  });
};

/**
 * Print maintenance record
 */
export const printMaintenancePDF = async (maintenance, motorcycle = null, lessee = null) => {
  await generateMaintenancePDF(maintenance, motorcycle, lessee);
};
